import { useEffect, useMemo, useState, useRef } from 'react';
import API from '@/lib/api';
import { X, Receipt, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

const fmt = (n) => `€ ${Number(n || 0).toFixed(2)}`;

/**
 * PartialCreditnotaModal — Laat de gebruiker per factuurlijn kiezen wat en hoeveel
 * hij wil crediteren. Toont live totaal + reeds-gecrediteerd context.
 */
export default function PartialCreditnotaModal({ docId, onClose, onCreated }) {
  const [src, setSrc] = useState(null);
  const [summary, setSummary] = useState(null);
  const [lijnen, setLijnen] = useState([]); // { ...origineel, credit_aantal, credit_prijs, selected }
  const [redenOpties, setRedenOpties] = useState([]);
  const [reden, setReden] = useState('');
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const contentRef = useRef(null);
  useEffect(() => {
    if (!src) return;
    const frame = requestAnimationFrame(() => contentRef.current?.querySelector('[data-testid="pcn-close"]')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [src]);

  useEffect(() => {
    (async () => {
      try {
        const [d, s, r] = await Promise.all([
          API.get(`/documenten/${docId}`),
          API.get(`/documenten/${docId}/creditnota-summary`).catch(() => ({ data: null })),
          API.get('/creditnota/redenen').catch(() => ({ data: { opties: [] } })),
        ]);
        setSrc(d.data);
        setSummary(s.data);
        setRedenOpties(r.data?.opties || []);
        setLijnen((d.data.lijnen || []).map(l => ({
          ...l,
          credit_aantal: l.aantal || 0,
          credit_prijs: l.prijs ?? l.bedrag ?? 0,
          selected: true,
        })));
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      }
    })();
  }, [docId]);

  const totals = useMemo(() => {
    const actief = lijnen.filter(l => l.selected && (l.credit_aantal || 0) > 0);
    const excl = actief.reduce((s, l) => s + (Number(l.credit_aantal) || 0) * (Number(l.credit_prijs) || 0), 0);
    const btwT = actief.reduce((s, l) => s + (Number(l.credit_aantal) || 0) * (Number(l.credit_prijs) || 0) * (Number(l.btw) || 0) / 100, 0);
    return { excl, btwT, totaal: excl + btwT, count: actief.length };
  }, [lijnen]);

  const updLine = (i, patch) => setLijnen(p => p.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const selectAll = () => setLijnen(p => p.map(l => ({ ...l, selected: true, credit_aantal: l.aantal })));
  const selectNone = () => setLijnen(p => p.map(l => ({ ...l, selected: false })));

  const maxCredit = summary ? summary.openstaand : (src?.totaal || 0);
  const overschreden = totals.totaal > maxCredit + 0.005;

  const genereer = async () => {
    setBusy(true); setErr('');
    try {
      const payload = {
        lijnen: lijnen.filter(l => l.selected && (l.credit_aantal || 0) > 0).map(l => ({
          art_id: l.art_id || null,
          omschr: l.omschr || '',
          referentie: l.referentie || l.ref_search || '',
          aantal: Number(l.credit_aantal),
          prijs: Number(l.credit_prijs),
          btw: Number(l.btw) || 0,
          eenheid: l.eenheid || 'st',
          gewicht_kg: l.gewicht_kg || 0,
        })),
        nota: nota || undefined,
        credit_reden: reden || undefined,
      };
      const res = await API.post(`/documenten/${docId}/creditnota-partial`, payload);
      onCreated?.(res.data);
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Creditnota aanmaken mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose?.(); }}>
      <DialogContent ref={contentRef} aria-describedby={undefined} className="bg-white text-gray-900 w-[calc(100vw-24px)] max-w-4xl max-h-[92dvh] p-0 gap-0 flex flex-col overflow-hidden [&>button:last-child]:hidden" data-testid="partial-cn-modal"
        onCloseAutoFocus={e => { e.preventDefault(); document.querySelector(`[data-testid="more-actions-trigger-${CSS.escape(docId)}"]`)?.focus(); }}>
      <DialogTitle className="sr-only">Creditnota voor factuur {docId}</DialogTitle>
      {!src ? <div className="p-6 text-sm"><div role={err ? 'alert' : 'status'} data-testid="pcn-loading">{err || 'Laden…'}</div><button type="button" onClick={onClose} className="btn btn-secondary btn-sm mt-3" data-testid="pcn-loading-close">Sluiten</button></div> :
      <div className="w-full min-h-0 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-rose-600 text-white">
          <div>
            <h3 className="text-sm font-bold inline-flex items-center gap-2"><Receipt size={18} weight="bold" /> Creditnota voor factuur {src.id}</h3>
            <p className="text-[11px] opacity-90 mt-0.5">Selecteer lijnen en aantallen om te crediteren</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1 text-white" aria-label="Creditnotavenster sluiten" data-testid="pcn-close"><X size={18} /></button>
        </div>

        {/* Summary */}
        {summary && (
          <div className="px-5 py-2 bg-rose-50 border-b border-rose-200 text-xs flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span>Factuurtotaal: <strong>{fmt(summary.factuur_totaal)}</strong></span>
              <span>Reeds gecrediteerd: <strong className="text-rose-700">{fmt(summary.gecrediteerd)} ({summary.percentage}%)</strong></span>
              <span>Nog te crediteren (max.): <strong className="text-green-700">{fmt(summary.openstaand)}</strong></span>
            </div>
            <div className="flex gap-2">
              <button className="text-[10px] underline text-rose-700" onClick={selectAll} data-testid="pcn-select-all">Alles selecteren</button>
              <button className="text-[10px] underline text-gray-500" onClick={selectNone} data-testid="pcn-select-none">Niets</button>
            </div>
          </div>
        )}

        {err && <div className="px-5 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 font-bold">{err}</div>}

        {/* Lijnen */}
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 text-left">Ref.</th>
                <th className="px-3 py-2 text-left">Omschrijving</th>
                <th className="px-3 py-2 text-right w-20">Origineel</th>
                <th className="px-3 py-2 text-right w-24">Credit aantal</th>
                <th className="px-3 py-2 text-right w-24">Prijs</th>
                <th className="px-3 py-2 text-right w-14">BTW</th>
                <th className="px-3 py-2 text-right w-24">Subtotaal</th>
              </tr>
            </thead>
            <tbody>
              {lijnen.map((l, i) => {
                const sub = (Number(l.credit_aantal) || 0) * (Number(l.credit_prijs) || 0) * (1 + (Number(l.btw) || 0) / 100);
                return (
                  <tr key={i} className={`border-b border-gray-100 ${l.selected ? '' : 'opacity-40'}`}>
                    <td className="px-3 py-1.5 text-center"><input type="checkbox" className="w-4 h-4" checked={!!l.selected} onChange={e => updLine(i, { selected: e.target.checked })} data-testid={`pcn-sel-${i}`} /></td>
                    <td className="px-3 py-1.5 font-mono font-bold">{l.referentie || l.ref_search || '—'}</td>
                    <td className="px-3 py-1.5">{l.omschr}</td>
                    <td className="px-3 py-1.5 text-right text-gray-500">{l.aantal}</td>
                    <td className="px-3 py-1.5 text-right"><input type="number" step="0.01" min="0" max={l.aantal} className="field-input text-xs text-right" style={{ width: '80px' }} value={l.credit_aantal} onChange={e => updLine(i, { credit_aantal: e.target.value, selected: parseFloat(e.target.value) > 0 })} data-testid={`pcn-qty-${i}`} /></td>
                    <td className="px-3 py-1.5 text-right"><input type="number" step="0.01" className="field-input text-xs text-right" style={{ width: '80px' }} value={l.credit_prijs} onChange={e => updLine(i, { credit_prijs: e.target.value })} data-testid={`pcn-price-${i}`} /></td>
                    <td className="px-3 py-1.5 text-right">{l.btw || 0}%</td>
                    <td className="px-3 py-1.5 text-right font-bold font-mono">{fmt(sub)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-rose-50 font-bold text-sm">
                <td className="px-3 py-2" colSpan="4">{totals.count} lijn(en) geselecteerd</td>
                <td className="px-3 py-2 text-right text-[10px] text-gray-500">Excl</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(totals.excl)}</td>
                <td className="px-3 py-2 text-right text-[10px] text-gray-500">BTW</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(totals.btwT)}</td>
              </tr>
              <tr className="bg-rose-600 text-white font-bold text-sm">
                <td className="px-3 py-2" colSpan="6">Totaal te crediteren</td>
                <td colSpan="2" className="px-3 py-2 text-right font-mono">{fmt(totals.totaal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Waarschuwing bij overschrijding */}
        {overschreden && (
          <div className="px-5 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-800 inline-flex items-center gap-2">
            <Warning size={14} weight="fill" className="text-amber-600" />
            <span>Het totaal ({fmt(totals.totaal)}) overschrijdt het resterend te crediteren bedrag ({fmt(maxCredit)}).</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider shrink-0" style={{ width: '56px' }}>Reden</label>
            <select
              className="field-input text-xs flex-1 min-w-0"
              value={reden}
              onChange={e => setReden(e.target.value)}
              data-testid="pcn-reden"
            >
              <option value="">— Kies reden (optioneel) —</option>
              {redenOpties.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider shrink-0" style={{ width: '56px' }}>Nota</label>
            <input
              className="field-input text-xs flex-1 min-w-0"
              placeholder="Toelichting (optioneel, bv. Klant retourneerde 2 stuks op 15/04)"
              value={nota}
              onChange={e => setNota(e.target.value)}
              data-testid="pcn-nota"
            />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            <button className="btn btn-secondary btn-sm" onClick={onClose} data-testid="pcn-cancel">Annuleren</button>
            <button className="btn btn-primary btn-sm inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700" onClick={genereer} disabled={busy || totals.count === 0} data-testid="pcn-confirm">
              {busy ? 'Bezig...' : `Creditnota aanmaken (${fmt(totals.totaal)})`}
            </button>
          </div>
        </div>
      </div>}
      </DialogContent>
    </Dialog>
  );
}
